// =============================================================
// Home page mounts - non-hero interactive bits
// =============================================================

const { useState: hUseState, useEffect: hUseEffect, useRef: hUseRef } = React;

// -------- HOW IT WORKS (3-step animated) --------
function HowItWorks() {
  const [step, setStep] = hUseState(0);
  hUseEffect(() => {
    const id = setInterval(() => setStep(s => (s + 1) % 3), 3200);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="how-frame">
      {[
        {
          num: '01', title: 'Capture',
          desc: 'Hit record. Denticode listens for the whole visit and labels every speaker - provider, assistant, patient - in real time.',
          art: <HowArt1 active={step === 0} />,
        },
        {
          num: '02', title: 'Structure',
          desc: 'Templates auto-fill as the visit unfolds. Tooth numbers, depths, dates, and CDT codes extract instantly. Qualitative fields refresh every 15s.',
          art: <HowArt2 active={step === 1} />,
        },
        {
          num: '03', title: 'Ship',
          desc: 'The finalized note becomes a claim - attachments resolved, narrative drafted, fees pulled from the payer schedule. One click into your PMS.',
          art: <HowArt3 active={step === 2} />,
        },
      ].map((s, i) => (
        <div key={i} className="how-step" data-active={step === i}>
          <span className="how-beam" aria-hidden="true"></span>
          <span className="how-num">{s.num}</span>
          <h3>{s.title}</h3>
          <p>{s.desc}</p>
          <div className="how-step-art">{s.art}</div>
        </div>
      ))}
    </div>
  );
}

function HowArt1({ active }) {
  // Animated mic + sound rings
  return (
    <div style={{
      width:'100%', height:'100%', display:'grid', placeItems:'center',
      background:'radial-gradient(circle at 50% 60%, rgba(25,173,197,0.12), transparent 60%)',
      position:'relative',
    }}>
      <div style={{position:'relative', width:96, height:96}}>
        {[1,2,3].map(i => (
          <div key={i} style={{
            position:'absolute', inset:0,
            border:'1px solid rgba(25,173,197,0.4)',
            borderRadius:'50%',
            transform: 'scale(' + (1 + i*0.4) + ')',
            opacity: active ? 0.7 - i*0.18 : 0,
            transition:'opacity .5s ease',
            animation: active ? `ring-${i} 2.4s ease-out infinite ${i*0.3}s` : 'none',
          }}/>
        ))}
        <div style={{
          position:'absolute', inset:'18px', borderRadius:'50%',
          background:'linear-gradient(135deg, #22C0DA, #0F7689)',
          display:'grid', placeItems:'center',
          boxShadow:'0 10px 24px -6px rgba(25,173,197,0.5)',
          color:'#fff',
        }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Zm-7 9a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2a5 5 0 0 1-10 0H5Z"/></svg>
        </div>
      </div>
      <style>{`
        @keyframes ring-1 { 0% { transform: scale(1); opacity: 0.7; } 100% { transform: scale(2.2); opacity: 0; } }
        @keyframes ring-2 { 0% { transform: scale(1); opacity: 0.5; } 100% { transform: scale(2.4); opacity: 0; } }
        @keyframes ring-3 { 0% { transform: scale(1); opacity: 0.3; } 100% { transform: scale(2.6); opacity: 0; } }
      `}</style>
    </div>
  );
}

function HowArt2({ active }) {
  // Animated form fields filling in
  return (
    <div style={{padding: '16px 20px', display:'flex', flexDirection:'column', gap:8}}>
      {[
        { l: 'Tooth', v: '14', delay: 0 },
        { l: 'Site', v: 'Distal-Lingual', delay: 0.3 },
        { l: 'PD', v: '6 mm', delay: 0.6 },
        { l: 'BOP', v: '+', delay: 0.9 },
      ].map((f, i) => (
        <div key={i} style={{
          display:'grid', gridTemplateColumns:'70px 1fr', alignItems:'center', gap:8,
          fontSize:12.5, lineHeight:1,
          opacity: active ? 1 : 0.4,
          transition: `opacity .4s ease ${f.delay}s`,
        }}>
          <span style={{color:'var(--text-soft)', fontWeight:500}}>{f.l}</span>
          <span style={{
            background:'var(--teal-wash)', color:'var(--teal-deep)',
            padding:'5px 10px', borderRadius:6,
            fontFamily:'var(--font-mono)', fontWeight:600, width:'fit-content',
            opacity: active ? 1 : 0,
            transform: active ? 'translateX(0)' : 'translateX(-6px)',
            transition: `opacity .4s ease ${f.delay+0.2}s, transform .4s ease ${f.delay+0.2}s`,
          }}>{f.v}</span>
        </div>
      ))}
    </div>
  );
}

function HowArt3({ active }) {
  // Animated claim send
  return (
    <div style={{
      width:'100%', height:'100%',
      display:'flex', alignItems:'center', justifyContent:'center',
      gap:16, padding:'0 20px',
      flexDirection:'column',
    }}>
      <div style={{
        display:'flex', alignItems:'center', justifyContent:'center', gap:12, width:'100%',
      }}>
        <div style={{
          padding:'8px 14px', borderRadius:10,
          border:'1px solid var(--border)', background:'#fff',
          fontSize:11.5, fontWeight:600,
          fontFamily:'var(--font-mono)', color:'var(--text)',
        }}>D4341 · $258</div>
        <div style={{
          flex:1, height:1,
          background:`linear-gradient(to right, var(--teal) ${active ? '100%' : '0%'}, var(--border) 0)`,
          transition:'background 1s ease',
        }}></div>
        <div style={{
          padding:'8px 14px', borderRadius:10,
          background: active ? 'linear-gradient(135deg, #15803D, #0a5028)' : 'var(--surface-muted)',
          color: active ? '#fff' : 'var(--text-soft)',
          fontSize:11.5, fontWeight:600,
          transition:'background .4s ease, color .4s ease',
        }}>
          {active ? '✓ Sent to Aetna' : 'Aetna PPO'}
        </div>
      </div>
      <div style={{display:'flex', gap:6, flexWrap:'wrap', justifyContent:'center'}}>
        {['PA Rx','Perio','Narrative'].map((a,i)=>(
          <span key={a} style={{
            fontSize:10, padding:'2px 8px', borderRadius:999,
            background:'var(--teal-wash)', color:'var(--teal-deep)',
            opacity: active ? 1 : 0.4,
            transition:`opacity .4s ease ${i*0.15}s`,
          }}>✓ {a}</span>
        ))}
      </div>
    </div>
  );
}

// -------- CAPTURE feature art (decorative Lab preview) --------
function CaptureArt() {
  return (
    <div style={{padding:24, display:'flex', flexDirection:'column', gap:16, height:'100%'}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <div style={{display:'flex', alignItems:'center', gap:8, fontSize:12, fontWeight:600}}>
          <span style={{width:8,height:8,borderRadius:'50%',background:'var(--teal)', boxShadow:'0 0 0 3px var(--teal-wash)'}}></span>
          Lab · Op 3 · Patient: Hannah V.
        </div>
        <span style={{fontSize:11, color:'var(--text-soft)', fontFamily:'var(--font-mono)'}}>00:02:14</span>
      </div>

      <div style={{
        background:'var(--surface-subtle)',
        border:'1px solid var(--border)',
        borderRadius:'var(--r-md)',
        padding:14,
        display:'flex', alignItems:'center', gap:2,
        height:42,
      }}>
        {Array.from({length:64}).map((_,i)=>{
          const h = 4 + (Math.abs(Math.sin(i*0.4) + Math.cos(i*0.27)*0.6)*22);
          return <div key={i} style={{flex:1, height:h, background:'var(--teal)', opacity:0.4 + Math.abs(Math.sin(i*0.4))*0.6, borderRadius:1, marginInline:1}}/>
        })}
      </div>

      <MiniTranscript live={false} lines={[
        { speaker:'provider', text:'Tooth fourteen, distal-lingual, six millimeters, bleeding on probing.' },
        { speaker:'assistant', text:'PA radiograph shows bone loss approximately three to four millimeters.' },
        { speaker:'patient', text:'Will I be numb for this part?' },
      ]} />

      <div style={{
        background:'var(--surface)',
        border:'1px solid var(--border)',
        borderRadius:'var(--r-md)',
        padding:'12px 14px',
        display:'flex', flexDirection:'column', gap:8,
      }}>
        <div style={{fontSize:11, color:'var(--text-soft)', letterSpacing:'0.06em', textTransform:'uppercase', fontWeight:600}}>Template · Perio SRP</div>
        <div style={{display:'grid', gridTemplateColumns:'auto 1fr', gap:'4px 16px', fontSize:13, alignItems:'baseline'}}>
          <span style={{color:'var(--text-soft)'}}>Quadrant</span><span style={{fontWeight:500}}>Upper Right</span>
          <span style={{color:'var(--text-soft)'}}>Site</span><span style={{fontWeight:500}}>#14 Distal-Lingual</span>
          <span style={{color:'var(--text-soft)'}}>PD / BOP</span><span style={{fontWeight:500}}>6mm · +</span>
          <span style={{color:'var(--text-soft)'}}>Mobility</span><span style={{fontWeight:500}}>-</span>
        </div>
      </div>
    </div>
  );
}

// -------- CODE feature art (dark, big CDT chip + alts) --------
function CodeArt() {
  return (
    <div style={{padding:28, display:'flex', flexDirection:'column', gap:16, height:'100%', justifyContent:'center'}}>
      <CdtChip
        code="D4341"
        name="Periodontal scaling and root planing"
        sub="Four or more teeth per quadrant"
        fee="$258.00"
        attachments={[
          { name:'Perio chart', ok:true },
          { name:'PA radiograph', ok:true },
          { name:'Narrative', ok:false },
        ]}
        dark
      />
      <div style={{
        background:'rgba(255,255,255,0.04)',
        border:'1px solid rgba(255,255,255,0.08)',
        borderRadius:14,
        padding:'14px 18px',
        display:'flex', alignItems:'center', gap:14,
      }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6BD7E8" strokeWidth="2"><path d="M12 2L2 7v10l10 5 10-5V7L12 2z"/><path d="M2 7l10 5 10-5M12 22V12"/></svg>
        <div style={{flex:1}}>
          <div style={{fontSize:13, fontWeight:600, color:'#fff'}}>Alternatives considered</div>
          <div style={{fontSize:12, color:'rgba(255,255,255,0.6)', marginTop:2}}>
            D4342 (1–3 teeth) ruled out - re-eval covers full UR quadrant.
          </div>
        </div>
        <span style={{fontFamily:'var(--font-mono)', fontSize:11, color:'rgba(255,255,255,0.45)'}}>view sources</span>
      </div>
    </div>
  );
}

// -------- BILL feature art (claim card) --------
function BillArt() {
  return (
    <div style={{padding:24, height:'100%', display:'flex', alignItems:'center'}}>
      <ClaimCard />
    </div>
  );
}

// -------- BEFORE / AFTER --------
function BeforeAfter() {
  const oldText = `5/18  Pt 14yr female, c/c "tooth feels rough on the upper".
  -PA, BWX no caries, mild gen marginal gingivitis.
  -Probing UR quad: 14 DL 6mm BOP, M 4 BOP. 15 M 8 BOP mob 1. Calculus subging.
  -Dx: localized periodontitis UR.
  -Tx today: D4341 UR, LA 0.5% bupiv. Pt tol well, instr OHI.
  -RTC 4-6wks for re-eval.
                                          -SP/lh`;
  const newText = `Periodontal Re-Evaluation · Upper Right
═════════════════════════════════════════
Chief complaint:    Roughness on upper teeth
Diagnosis:          Localized periodontitis, UR

Findings:
  • #14 DL - 6mm PD, BOP+
  • #15 M  - 8mm PD, BOP+, mobility 1
  • Bone loss 3–4mm on PA (assistant note)
  • Subgingival calculus, UR quadrant

Treatment performed:
  • D4341  SRP, 4+ teeth/quadrant - UR
  • D9215  Local anesthesia (bupivacaine 0.5%)

Patient education:  OHI reviewed.
Recall:             4–6 weeks for re-eval.
Provider:           Dr. S. Patel`;

  return (
    <div className="ba-frame">
      <div className="ba-card ba-card--old">
        <div className="ba-header">
          <span className="ba-title">Typed after the visit</span>
          <span className="ba-time">⏱ 12 min 04s</span>
        </div>
        <pre className="ba-body" style={{margin:0}}>{oldText}</pre>
        <div className="ba-footer">
          <span className="ba-stat"><strong>1</strong>/2 codes captured</span>
          <span className="ba-stat" style={{color:'var(--warning)'}}>Missing PA citation</span>
          <span className="ba-stat" style={{color:'var(--danger)'}}>Mobility narrative truncated</span>
        </div>
      </div>
      <div className="ba-card ba-card--new">
        <div className="ba-header">
          <span className="ba-title">Written by Denticode</span>
          <span className="ba-time">⏱ 6s · auto-finalized</span>
        </div>
        <pre className="ba-body" style={{margin:0, fontSize:12.5}}>{newText}</pre>
        <div className="ba-footer">
          <span className="ba-stat" style={{color:'var(--success)'}}><strong>2</strong>/2 codes captured</span>
          <span className="ba-stat" style={{color:'var(--teal-deep)'}}>✓ PA + perio chart attached</span>
          <span className="ba-stat" style={{color:'var(--teal-deep)'}}>+ $32 recovered</span>
        </div>
      </div>
    </div>
  );
}

// -------- mount all --------
function mountIfExists(id, el) {
  const node = document.getElementById(id);
  if (node) ReactDOM.createRoot(node).render(el);
}

mountIfExists('how-mount', <HowItWorks />);
mountIfExists('capture-mount', <CaptureArt />);
mountIfExists('code-mount', <CodeArt />);
mountIfExists('bill-mount', <BillArt />);
mountIfExists('ba-mount', <BeforeAfter />);
